import { useState, useEffect } from 'react';
import { useJournalistDialog } from '@/contexts/JournalistDialogContext';
import { useBrandManager } from '@/contexts/BrandProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

// Helper to sync journalist to os_contacts
const syncJournalistToContacts = async (
  brandId: string,
  journalist: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    organization: string;
    title: string;
  }
): Promise<string | null> => {
  if (!journalist.email) return null;

  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: journalist.email,
      p_first_name: journalist.first_name || null,
      p_last_name: journalist.last_name || null,
      p_phone: journalist.phone || null,
      p_module: ['pr'],
      p_activity: ['journalist'],
      p_organization: journalist.organization || null,
      p_title: journalist.title || null,
      p_user_id: null,
    });

    if (error) {
      console.error('Error syncing journalist to contacts:', error);
      return null;
    }
    return contactId;
  } catch (err) {
    console.error('Exception syncing journalist to contacts:', err);
    return null;
  }
};

const emptyFormData = {
  first_name: '',
  last_name: '',
  organization: '',
  title: '',
  email: '',
  handle: '',
  sector: '',
  language: '',
  linkedin: '',
  location: '',
  phone: '',
};

export const GlobalJournalistDialog = () => {
  const { isOpen, closeDialog, editingJournalist, onSuccess } = useJournalistDialog();
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);

  // Initialize form data when dialog opens or editingJournalist changes
  useEffect(() => {
    if (isOpen && editingJournalist) {
      setFormData({
        first_name: editingJournalist.first_name || '',
        last_name: editingJournalist.last_name || '',
        organization: editingJournalist.organization || '',
        title: editingJournalist.title || '',
        email: editingJournalist.email || '',
        handle: editingJournalist.handle || '',
        sector: editingJournalist.sector || '',
        language: editingJournalist.language || '',
        linkedin: (editingJournalist as any).linkedin || '',
        location: (editingJournalist as any).location || '',
        phone: (editingJournalist as any).phone || '',
      });
    } else if (isOpen && !editingJournalist) {
      setFormData(emptyFormData);
    }
  }, [isOpen, editingJournalist]);

  const handleClose = () => {
    setFormData(emptyFormData);
    closeDialog();
  };

  const handleSubmit = async () => {
    if (!currentBrandId) {
      toast({
        title: 'Error',
        description: 'No brand selected',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.email && !formData.first_name && !formData.last_name) {
      toast({
        title: 'Error',
        description: 'Please provide at least an email or name',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Sync journalist to os_contacts first
      const contactId = await syncJournalistToContacts(currentBrandId, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        title: formData.title,
      });

      if (editingJournalist) {
        // Update existing journalist
        const { error } = await supabase
          .from('os_journalists')
          .update({
            first_name: formData.first_name,
            last_name: formData.last_name,
            organization: formData.organization,
            title: formData.title,
            email: formData.email,
            handle: formData.handle,
            sector: formData.sector,
            language: formData.language,
            linkedin: formData.linkedin,
            location: formData.location,
            phone: formData.phone,
            contact_id: contactId,
          })
          .eq('id', editingJournalist.id);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Journalist updated successfully',
        });
      } else {
        // Create new journalist
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData?.user?.id;

        const { error } = await supabase
          .from('os_journalists')
          .insert({
            ...formData,
            brand_id: currentBrandId,
            user_id: userId,
            contact_id: contactId,
          });

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Journalist created successfully',
        });
      }

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save journalist',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditing = !!editingJournalist;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Journalist' : 'Create New Journalist'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update journalist information' : 'Add a new journalist to your database'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first-name">First Name</Label>
            <Input
              id="first-name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="John"
            />
          </div>
          <div>
            <Label htmlFor="last-name">Last Name</Label>
            <Input
              id="last-name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Doe"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
            />
          </div>
          <div>
            <Label htmlFor="organization">Organization</Label>
            <Input
              id="organization"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              placeholder="The Daily News"
            />
          </div>
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Senior Reporter"
            />
          </div>
          <div>
            <Label htmlFor="handle">Handle/Username</Label>
            <Input
              id="handle"
              value={formData.handle}
              onChange={(e) => setFormData({ ...formData, handle: e.target.value })}
              placeholder="@johndoe"
            />
          </div>
          <div>
            <Label htmlFor="sector">Sector</Label>
            <Input
              id="sector"
              value={formData.sector}
              onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
              placeholder="Technology"
            />
          </div>
          <div>
            <Label htmlFor="language">Language</Label>
            <Input
              id="language"
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              placeholder="English"
            />
          </div>
          <div>
            <Label htmlFor="linkedin">LinkedIn</Label>
            <Input
              id="linkedin"
              value={formData.linkedin}
              onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
              placeholder="https://linkedin.com/in/johndoe"
            />
          </div>
          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="New York, USA"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1 234 567 8900"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditing ? 'Update Journalist' : 'Create Journalist'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};